import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Spin, Button, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faGears } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateOpenMonitoringDeviceConfigurationDrawer } from "../../../redux/monitoring/monitoringSlice";
// INTERFACES
import { RootState } from "../../../app/store";
import { MessageTypeInterface } from "../../../interfaces/MessageType.interface";

interface DeviceStatus {
	id: string | number;
	name: string;
	elevators: number;
	escalators: number;
	movingwalks: number;
	gpio: number;
	color: string;
	active: boolean;
}

export default function MonitoringDeviceViewHeader({
	totalUnites,
	deviceMessageTypes,
}: {
	totalUnites: { elevators: number; escalators: number; movingwalks: number; gpio: number };
	deviceMessageTypes: MessageTypeInterface[];
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [data, setData] = useState<DeviceStatus[]>([]);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { monitoringDeviceConfiguration } = useSelector((state: RootState) => state.monitoring);

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const buildData = () => {
		const newData: DeviceStatus[] = [];
		deviceMessageTypes.forEach((message: MessageTypeInterface) => {
			newData.push({
				id: message.id,
				name: message.name,
				elevators: 0,
				escalators: 0,
				movingwalks: 0,
				gpio: 0,
				color: message.color || "gray",
				active: message.active || false,
			});
		});
		setData(newData);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	}, [monitoringDeviceConfiguration]);

	useEffect(() => {
		if (deviceMessageTypes) {
			buildData();
		}
	}, [deviceMessageTypes]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Spin spinning={isLoading}>
			<div className='monitoring-header'>
				<div className='status-column status-strip generalStyles__spaceBetween' style={{ padding: "10px" }}>
					<div>
						<span className='generalStyles__info' style={{ fontSize: "22px", fontWeight: "bolder" }}>
							{monitoringDeviceConfiguration.viewType === "server" ? t("general.servers") : t("general.banks")}
						</span>
					</div>
					<div>
						<Button
							shape='round'
							icon={<FontAwesomeIcon icon={faGears} style={{ color: "gray", marginRight: "5px" }} />}
							onClick={() => {
								dispatch(updateOpenMonitoringDeviceConfigurationDrawer(true));
							}}
						>
							Configurations
						</Button>
					</div>
				</div>
				<div className='status-strip'>
					<div className='status-column'>
						<div className='status-header'>
							<FontAwesomeIcon icon={faCircle} style={{ color: "gray", marginRight: "5px" }} />
							Type
						</div>
						<div className='status-tags'>
							{monitoringDeviceConfiguration.showElevators && (
								<Tag color='blue' className='status-tag '>
									Elevators
								</Tag>
							)}
							{monitoringDeviceConfiguration.showEscalators && (
								<Tag color='volcano' className='status-tag' style={{ marginTop: "5px" }}>
									Escalators
								</Tag>
							)}
							{monitoringDeviceConfiguration.showMovingwalks && (
								<Tag color='green' className='status-tag' style={{ marginTop: "5px" }}>
									Movingwalks
								</Tag>
							)}
							{monitoringDeviceConfiguration.showGpio && (
								<Tag color='purple' className='status-tag' style={{ marginTop: "5px" }}>
									GPIO
								</Tag>
							)}
						</div>
					</div>
					<div className='status-column'>
						<div className='status-header'>
							<FontAwesomeIcon icon={faCircle} style={{ color: "blue", marginRight: "5px" }} />
							TOTAL
						</div>
						<div className=''>
							{monitoringDeviceConfiguration.showElevators && (
								<div className='status-cell' data-unit='elevators'>
									<span className='value'>{totalUnites.elevators}</span>
								</div>
							)}
							{monitoringDeviceConfiguration.showEscalators && (
								<div className='status-cell' data-unit='escalators'>
									<span className='value'>{totalUnites.escalators}</span>
								</div>
							)}
							{monitoringDeviceConfiguration.showMovingwalks && (
								<div className='status-cell' data-unit='movingwalks'>
									<span className='value'>{totalUnites.movingwalks}</span>
								</div>
							)}
							{monitoringDeviceConfiguration.showGpio && (
								<div className='status-cell' data-unit='gpio'>
									<span className='value'>{totalUnites.gpio}</span>
								</div>
							)}
						</div>
					</div>
					{data.map((status: DeviceStatus) => (
						<>
							{status.active && (
								<div key={status.name} className='status-column generalStyles__textEllipsis'>
									<div className='generalStyles__flexCenter' style={{ padding: "10px" }}>
										<div>
											<FontAwesomeIcon icon={faCircle} style={{ color: status.color, marginRight: "5px" }} />
										</div>
										<div className='generalStyles__textEllipsis generalStyles__hoverCursor generalStyles__textCenter'>
											<Tooltip title={status.name}>{status.name}</Tooltip>
										</div>
									</div>
									<div className='status-values'>
										{monitoringDeviceConfiguration.showElevators && (
											<div className='status-cell' data-unit='elevators'>
												<span className='value'>{status.elevators}</span>
											</div>
										)}
										{monitoringDeviceConfiguration.showEscalators && (
											<div className='status-cell' data-unit='escalators'>
												<span className='value'>{status.escalators}</span>
											</div>
										)}
										{monitoringDeviceConfiguration.showMovingwalks && (
											<div className='status-cell' data-unit='movingwalks'>
												<span className='value'>{status.movingwalks}</span>
											</div>
										)}
										{monitoringDeviceConfiguration.showGpio && (
											<div className='status-cell' data-unit='gpio'>
												<span className='value'>{status.gpio}</span>
											</div>
										)}
									</div>
								</div>
							)}
						</>
					))}
				</div>
			</div>
		</Spin>
	);
}
